import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { apiRequest } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function CRUDPage({ 
  title, 
  apiEndpoint, 
  fields, 
  dataKey,
  icon: Icon,
  softDelete = true  // Enable soft delete by default
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/${apiEndpoint}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch items');
      }

      setItems(response.data[dataKey] || []);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({});
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const data = {};
    fields.forEach(field => {
      data[field.key] = item[field.key] || '';
    });
    setFormData(data);
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/${apiEndpoint}/${item.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to deactivate item');
      }

      setDeleteConfirm(null);
      fetchItems();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRestore = async (item) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/${apiEndpoint}/${item.id}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to restore item');
      }

      fetchItems();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const token = localStorage.getItem('admin_token');
      const method = editingItem ? 'PUT' : 'POST';
      const url = editingItem 
        ? `${BACKEND_URL}/api/admin/${apiEndpoint}/${editingItem.id}`
        : `${BACKEND_URL}/api/admin/${apiEndpoint}`;

      const response = await apiRequest(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(response.data?.detail || 'Failed to save item');
      }

      setShowModal(false);
      setFormData({});
      setEditingItem(null);
      fetchItems();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Separate active and inactive items
  const activeItems = items.filter(item => item.is_active !== false);
  const inactiveItems = items.filter(item => item.is_active === false);

  const renderTable = (itemList, isInactive = false) => (
    <div className={`bg-white rounded-2xl shadow-sm overflow-hidden ${isInactive ? 'opacity-75' : ''}`}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {fields.map((field, index) => (
                <th key={index} className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {field.label}
                </th>
              ))}
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {itemList.map((item) => (
              <tr key={item.id} className={`hover:bg-gray-50 ${isInactive ? 'bg-gray-50/50' : ''}`}>
                {fields.map((field, index) => (
                  <td key={index} className={`px-6 py-4 text-sm ${isInactive ? 'text-gray-500' : 'text-gray-900'}`}>
                    {field.render ? field.render(item[field.key]) : (item[field.key] || '-')}
                  </td>
                ))}
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isInactive
                      ? 'bg-red-100 text-red-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {isInactive ? 'Inactive' : 'Active'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex gap-2">
                    {isInactive ? (
                      <button
                        onClick={() => handleRestore(item)}
                        className="text-green-600 hover:text-green-800 flex items-center gap-1"
                        title="Restore"
                      >
                        <RotateCcw className="w-4 h-4" />
                        <span className="text-xs">Restore</span>
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(item)}
                          className="text-red-600 hover:text-red-800"
                          title="Deactivate"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {itemList.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          {isInactive ? 'No inactive items' : 'No items found'}
        </div>
      )}
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-8 h-8" style={{ color: '#7D64FA' }} />}
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#7D64FA' }}>{title}</h1>
            <p className="text-sm text-gray-600">Active: {activeItems.length} | Inactive: {inactiveItems.length}</p>
          </div>
        </div>
        <Button
          onClick={handleAdd}
          className="text-white hover:opacity-90 rounded-full"
          style={{ backgroundColor: '#7D64FA' }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#7D64FA' }}></div>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
          {Icon && <Icon className="w-16 h-16 text-gray-400 mx-auto mb-4" />}
          <h3 className="text-xl font-bold mb-2" style={{ color: '#7D64FA' }}>No Items Yet</h3>
          <p className="text-gray-600 mb-6">Click "Add New" to create your first item.</p>
        </div>
      ) : (
        <>
          {/* Active Items */}
          <div className="mb-8">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: '#7D64FA' }}>
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              Active {title} ({activeItems.length})
            </h2>
            {renderTable(activeItems, false)}
          </div>

          {/* Inactive Items */}
          {inactiveItems.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-gray-600 mb-4 flex items-center gap-2">
                <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                Inactive {title} ({inactiveItems.length})
              </h2>
              {renderTable(inactiveItems, true)}
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold" style={{ color: '#7D64FA' }}>
                {editingItem ? 'Edit' : 'Add'} {title.slice(0, -1)}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {fields.filter(field => field.showInForm !== false).map((field, index) => (
                <div key={index}>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#7D64FA' }}>
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      name={field.key}
                      value={formData[field.key] || ''}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': '#7D64FA' }}
                      placeholder={field.placeholder}
                      required={field.required}
                    />
                  ) : (
                    <input
                      type={field.type || 'text'}
                      name={field.key}
                      value={formData[field.key] || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent"
                      placeholder={field.placeholder}
                      required={field.required}
                    />
                  )}
                </div>
              ))}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 rounded-full"
                  style={{ color: '#7D64FA' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 text-white hover:opacity-90 rounded-full"
                  style={{ backgroundColor: '#7D64FA' }}
                >
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4" style={{ color: '#7D64FA' }}>Confirm Deactivate</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to deactivate "{deleteConfirm[fields[0].key]}"? It will be moved to the inactive list and can be restored later.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 rounded-full"
                style={{ color: '#7D64FA' }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 bg-red-500 text-white hover:bg-red-600 rounded-full"
              >
                Deactivate
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
